import { useNotifyUserConsentStorage } from '../../../../../../AppClients';
import { allowSwitchUser } from '../../../../../../backend/features/permissions/shared';
import { LabelTypography } from '../../../../../../ui/components/LabelTypography';
import { useUserState } from '../../../../../../ui/hooks';
import { useContactManagementTranslation } from '../../../../../../ui/hooks/useContactManagementTranslation';
import { getUserStateResponseOptional } from '../../../../../../ui/providers';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { Box, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { AccountCompletionBar } from '../../AccountCompletionBar';
import { AccountContactEmails, AccountPagerNumber, AccountPhoneNumber } from '../../AccountDetails';
import { AccountProfilePicture } from '../../AccountProfilePicture';
import { AccountStatusChip } from '../../AccountStatusChip';
import { AccountProfileHeaderActions } from './AccountProfileHeaderActions';
export const AccountProfileHeader = ({ account, headerSubTitle, accountPermissions, additionalHeaderContent, completionBarTitle, }) => {
    var _a, _b, _c;
    const { t } = useContactManagementTranslation();
    const location = useLocation();
    const showBack = (_a = location.state) === null || _a === void 0 ? void 0 : _a.back;
    const userState = useUserState();
    const userStateResponse = useSelector(getUserStateResponseOptional);
    const showSwitchToUser = (userState === null || userState === void 0 ? void 0 : userState.id) !== account.id &&
        allowSwitchUser(userState) &&
        ['active', 'inactive'].includes(account.data.systemStatus) &&
        !(userStateResponse === null || userStateResponse === void 0 ? void 0 : userStateResponse.switchUserPrev);
    const notifyUserConsentClient = useNotifyUserConsentStorage();
    const [phoneConsents, setPhoneConsents] = React.useState([]);
    const downSmScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const downMdScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const upLgScreen = useMediaQuery((theme) => theme.breakpoints.up('lg'));
    React.useEffect(() => {
        var _a;
        if (accountPermissions.allowUpdateFull && ((_a = account.data.contactPhoneNumbers) === null || _a === void 0 ? void 0 : _a.length)) {
            notifyUserConsentClient
                .list({
                filter: [
                    {
                        column: 'phoneNumber',
                        op: 'in',
                        value: account.data.contactPhoneNumbers.map((contactPhoneNumber) => contactPhoneNumber.phoneNumber),
                    },
                ],
            })
                .then((response) => setPhoneConsents(response.rows))
                .catch(handleErrorSnackbar);
        }
    }, [notifyUserConsentClient, account.data.contactPhoneNumbers, accountPermissions.allowUpdateFull]);
    return (React.createElement(Box, { sx: [{ py: 3, display: 'flex', flexDirection: 'column', gap: 2 }, downSmScreen ? { px: 2 } : { px: 4 }], "data-test": 'account-page-header' },
        (showBack || showSwitchToUser) && (React.createElement(AccountProfileHeaderActions, { accountEmail: account.data.email, showBack: showBack, showSwitchToUser: showSwitchToUser })),
        React.createElement(Box, { sx: [
                { display: 'flex', width: '100%', justifyContent: 'space-between' },
                downMdScreen ? { flexWrap: 'wrap' } : { flexWrap: 'nowrap' },
                downSmScreen ? { flexDirection: 'column' } : { gap: 3, flexDirection: 'row', alignItems: 'center' },
            ] },
            React.createElement(Box, { sx: [
                    { display: 'flex', gap: 4, justifyContent: 'space-between' },
                    downSmScreen ? {} : { py: 0.75, alignItems: 'center' },
                ] },
                !downSmScreen && (React.createElement(AccountProfilePicture, { accountId: account.id, firstName: account.data.firstName, lastName: account.data.lastName, orgMembership: account.columns.orgMembership })),
                React.createElement(Box, { sx: { display: 'flex', gap: 2, flexDirection: 'column' } },
                    React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.25 } },
                        React.createElement(Box, { sx: [
                                { display: 'flex', alignItems: 'center', p: 0, mb: 0.5 },
                                upLgScreen ? { gap: 2, minWidth: '432px' } : { gap: 0.5 },
                            ] },
                            React.createElement(LabelTypography, { variant: 'h1', "data-test": 'account-name' }, account.columns.displayName),
                            React.createElement(AccountStatusChip, { status: account.data.systemStatus, label: t(`panels.accounts.status.${account.data.systemStatus}`) })),
                        headerSubTitle && (React.createElement(Box, { sx: { alignItems: 'center', display: 'flex' } },
                            React.createElement(Typography, { variant: 'body2' }, headerSubTitle)))),
                    React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' } }, (_b = account.data.contactEmails) === null || _b === void 0 ? void 0 :
                        _b.map((email, index) => (React.createElement(AccountContactEmails, { key: email.email, contactEmail: email, bouncedEmails: account === null || account === void 0 ? void 0 : account.columns.bouncedEmails, showLabel: false, "data-test": `account-email-${index}` }))),
                        account.data.contactPhoneNumbers &&
                            account.data.contactPhoneNumbers.map((contactPhoneNumber, index) => (React.createElement(AccountPhoneNumber, { key: contactPhoneNumber.phoneNumber, contactPhoneNumber: contactPhoneNumber, phoneConsents: phoneConsents, showLabel: false, "data-test": `contact-phone-number-${index}` }))), (_c = account.data.pagerNumbers) === null || _c === void 0 ? void 0 :
                        _c.map((pagerNumber, index) => (React.createElement(AccountPagerNumber, { key: index, pagerNumber: pagerNumber, "data-test": `contact-pager-number-${index}` })))))),
            React.createElement(Box, { sx: [
                    {
                        alignItems: 'center',
                        display: 'flex',
                        width: '100%',
                    },
                    downSmScreen ? { mt: 3.5, flexDirection: 'row', gap: 2 } : { flexDirection: 'column', mt: 0, gap: 3 },
                ] },
                downSmScreen && (React.createElement(AccountProfilePicture, { accountId: account.id, firstName: account.data.firstName, lastName: account.data.lastName, orgMembership: account.columns.orgMembership })),
                accountPermissions.allowUpdateFull && (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', width: '100%' } },
                    React.createElement(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: downSmScreen ? 'column' : 'row',
                            maxWidth: 308,
                            width: '100%',
                        }, "data-test": 'account-completion' },
                        React.createElement(AccountCompletionBar, { title: completionBarTitle !== null && completionBarTitle !== void 0 ? completionBarTitle : t('panels.accounts.completion'), account: account })),
                    !downSmScreen && additionalHeaderContent))),
            downSmScreen && additionalHeaderContent)));
};
